import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import PropTypes from 'prop-types';
import objectId from 'bson-objectid';
import InputMask from 'react-input-mask';
import DatePicker, { registerLocale } from 'react-datepicker';
import { Switch } from 'pretty-checkbox-react';
/* actions */
import { getAllDepartments } from '../../redux/actions/departments';
import { getAllDevices } from '../../redux/actions/devices';
import {
  getAllIVRs,
  getAllIVRsOptions,
  editIVRsOptions
} from '../../redux/actions/ivrs';
import ErrorMsgField from '../../components/ErrorMsgField';
/* components */
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
/* form-validation */
import schema from '../../form-validation/ivrOptions';
import ptBR from 'date-fns/locale/pt-BR';
registerLocale('ptBR', ptBR);

function Options (props) {
  const { id } = props.match.params;
  const { register, handleSubmit, errors } = useForm({
    validationSchema: schema,
    reValidateMode: 'onSubmit'
  });
  const ivrs = useSelector(state => state.ivrs);
  const departments = useSelector(state => state.departments.data);
  const ivr = ivrs.data.find(ivr => ivr._id === id);
  const devices = useSelector(state =>
    state.devices.data
      .filter(device => device.type === 'G')
      .sort((a, b) =>
        a.number.toLowerCase().localeCompare(b.number.toLowerCase())
      )
  );
  const dispatch = useDispatch();
  const formatTime = time => {
    const tm = String(time);
    const qtdDigits = tm.length;
    switch (qtdDigits) {
      case 1:
        return '000' + tm;
      case 2:
        return '00' + tm;
      case 3:
        return '0' + tm;
      default:
        return tm;
    }
  };
  const INITIAL_VALUES = {
    _id: id || '',
    name: '',
    department: '',
    deviceId: '',
    description: '',
    waitsec: 0,
    saydigits: false
  };
  const [data, setData] = useState(INITIAL_VALUES);
  const [defaultAction, setDefaultAction] = useState({
    ivr: id,
    default: true,
    action: { action: 0, destination: '' }
  });
  const [actions, setActions] = useState([]);
  const [digits, setDigits] = useState([]);
  const submitButton = useRef();

  useEffect(() => {
    dispatch(getAllDepartments());
    dispatch(getAllDevices());
    dispatch(getAllIVRs());
    dispatch(getAllIVRsOptions());
  }, [dispatch]);

  useEffect(() => {
    if (ivr) {
      const newData = {
        ...INITIAL_VALUES,
        ...ivr,
        department: ivr.department._id || '',
        deviceId: (ivr.deviceId && ivr.deviceId._id) || ''
      };
      setData(newData);
    }
  }, [ivr]);

  useEffect(() => {
    const options = ivrs.ivrOptions.filter(
      ivrOption => String(ivrOption.ivr) === String(id)
    );
    if (options.length) {
      const opts = [...options];
      let defaultActionPosition = -1;
      const defaultAction = opts.find((op, position) => {
        const keys = Object.keys(op);
        if (
          op.default &&
          !(keys.includes('len') || keys.includes('digits')) &&
          !(keys.includes('hourStart') || keys.includes('hourEnd'))
        ) {
          defaultActionPosition = position;
          return true;
        } else {
          return false;
        }
      });
      if (defaultActionPosition >= 0) {
        opts.splice(defaultActionPosition, 1);
        setDefaultAction({ ...defaultAction });
      }

      const digitsPosition = [];
      const _digits = opts.filter((op, position) => {
        const keys = Object.keys(op);
        if (keys.includes('len') || keys.includes('digits')) {
          digitsPosition.push(position);
          return true;
        } else {
          return false;
        }
      });
      digitsPosition.reverse().forEach(position => opts.splice(position, 1));

      const dateFormatField = obj => {
        const keys = Object.keys(obj);
        if (keys.includes('dateStart') || keys.includes('dateEnd')) {
          const dSY = parseInt(String(obj.dateStart).slice(0, 4), 10);
          const dSM = parseInt(String(obj.dateStart).slice(4, 6), 10) - 1;
          const dSD = parseInt(String(obj.dateStart).slice(6, 8), 10);
          const dS = new Date();
          dS.setFullYear(dSY, dSM, dSD);
          obj.dateStart = dS;

          const dEY = parseInt(String(obj.dateEnd).slice(0, 4), 10);
          const dEM = parseInt(String(obj.dateEnd).slice(4, 6), 10) - 1;
          const dED = parseInt(String(obj.dateEnd).slice(6, 8), 10);
          const dE = new Date();
          dE.setFullYear(dEY, dEM, dED);
          obj.dateEnd = dE;
        }
        return obj;
      };

      const digits = _digits.map(dateFormatField);
      if (digitsPosition.length) {
        setDigits([...digits]);
      }

      const actions = opts.map(dateFormatField);
      if (actions.length) {
        setActions([...actions]);
      }
    }
  }, [ivrs.ivrOptions, id]);

  const submit = () => {
    const formatDate = (year, month, day) => {
      const _month = parseInt(month, 10) < 10 ? '0' + month : month;
      const _day = parseInt(day, 10) < 10 ? '0' + day : day;
      return `${year}${_month}${_day}`;
    };
    const formatFields = item => {
      const obj = { ...item };
      const keys = Object.keys(obj);
      if (keys.includes('dateStart') && keys.includes('dateEnd')) {
        const dSYear = obj.dateStart.getFullYear();
        const dSMonth = obj.dateStart.getMonth() + 1;
        const dSDay = obj.dateStart.getDate();
        const dateStart = formatDate(dSYear, dSMonth, dSDay);
        obj.dateStart = parseInt(dateStart, 10);

        const dEYear = obj.dateEnd.getFullYear();
        const dEMonth = obj.dateEnd.getMonth() + 1;
        const dEDay = obj.dateEnd.getDate();
        const dateEnd = formatDate(dEYear, dEMonth, dEDay);
        obj.dateEnd = parseInt(dateEnd, 10);
      }
      return obj;
    };
    const newActions = actions.map(formatFields);
    const newDigits = digits.map(formatFields);
    const newData = {
      ivr: {
        ...data,
        deviceId: !data.deviceId ? null : data.deviceId,
        waitsec: data.waitsec ? Number(data.waitsec) : 0
      },
      ivrOptions: [{ ...defaultAction }]
        .concat([...newActions])
        .concat([...newDigits])
    };
    dispatch(editIVRsOptions(newData, submitButton.current));
  };

  const handleChange = evt => {
    evt.preventDefault();
    if (evt.target.getAttribute('number') === 'on') {
      if (evt.target.value.match(/^\d+$/) || evt.target.value === '') {
        setData({
          ...data,
          [evt.target.name]: evt.target.value
        });
      } else {
        evt.target.value = data[evt.target.name];
      }
    } else {
      const isCheckbox = evt.target.type === 'checkbox';
      setData({
        ...data,
        [evt.target.name]: isCheckbox ? evt.target.checked : evt.target.value
      });
    }
  };

  const handleChangeActionDefault = evt => {
    evt.preventDefault();
    const { name, value } = evt.target;
    if (name === 'action') {
      const typeAction = parseInt(value, 10);
      setDefaultAction({
        ...defaultAction,
        action: { action: typeAction, destination: '' }
      });
    } else {
      if (evt.target.getAttribute('number') === 'on') {
        if (evt.target.value.match(/^\d+$/) || evt.target.value === '') {
          setDefaultAction({
            ...defaultAction,
            action: {
              action: defaultAction.action.action,
              [name]: value
            }
          });
        } else {
          evt.target.value = defaultAction.action.destination;
        }
      } else {
        setDefaultAction({
          ...defaultAction,
          action: {
            action: defaultAction.action.action,
            [name]: value
          }
        });
      }
    }
  };

  const handleClickAddAction = () => {
    const action = {
      _id: objectId().str,
      dayStart: 1,
      dayEnd: 5,
      hourStart: 0,
      hourEnd: 235959,
      ivr: data._id,
      action: {
        action: 0,
        destination: ''
      }
    };
    const actionFields = [...actions];
    actionFields.push(action);
    setActions([...actionFields]);
  };

  const handleClickRemoveAction = position => {
    const actionFields = [...actions];
    actionFields.splice(position, 1);
    setActions([...actionFields]);
  };

  const handleChangeActionsAction = (evt, position) => {
    evt.preventDefault();
    const { value } = evt.target;
    const typeAction = parseInt(value, 10);
    const actionFields = [...actions];
    actionFields[position].action = {
      action: typeAction,
      destination: '',
      url: '',
      method: 'GET',
      headers: [],
      data: []
    };
    setActions([...actionFields]);
  };

  const handleChangeActionsDestination = (evt, position) => {
    evt.preventDefault();
    const { value } = evt.target;
    const actionFields = [...actions];
    if (evt.target.getAttribute('number') === 'on') {
      if (evt.target.value.match(/^\d+$/) || evt.target.value === '') {
        actionFields[position].action.destination = value;
        setActions([...actionFields]);
      } else {
        evt.target.value = actionFields[position].action.destination;
      }
    } else {
      actionFields[position].action.destination = value;
      setActions([...actionFields]);
    }
  };

  const handleChangeActionsScheduling = (evt, position) => {
    evt.preventDefault();
    const { value } = evt.target;
    const actionFields = [...actions];
    const action = { ...actionFields[position] };
    if (value === 'interval') {
      delete action.dayStart;
      delete action.dayEnd;
      action.dateStart = new Date();
      action.dateEnd = new Date();
      actionFields[position] = action;
      setActions([...actionFields]);
    } else {
      delete action.dateStart;
      delete action.dateEnd;
      action.dayStart = 1;
      action.dayEnd = 5;
      actionFields[position] = action;
      setActions([...actionFields]);
    }
  };

  const handleChangeActionsDay = (evt, position) => {
    evt.preventDefault();
    const { name, value } = evt.target;
    const actionFields = [...actions];
    actionFields[position][name] = value;
    setActions([...actionFields]);
  };

  const handleChangeActionsDate = (evt, position) => {
    const { name, value } = evt.target;
    const actionFields = [...actions];
    actionFields[position][name] = value;
    setActions([...actionFields]);
  };

  const handleChangeActionsHour = (evt, position) => {
    evt.preventDefault();
    const { name, value } = evt.target;
    const newValue = parseInt(value.replace(/:|_/g, ''), 10);
    const actionFields = [...actions];
    actionFields[position][name] = newValue;
    setActions([...actionFields]);
  };

  const handleChangeActionsMethod = (evt, position) => {
    const { value } = evt.target;
    const actionFields = [...actions];
    actionFields[position].action.method = value;
    setActions([...actionFields]);
  };

  const handleChangeActionsURL = (evt, position) => {
    const { value } = evt.target;
    const actionFields = [...actions];
    actionFields[position].action.url = value;
    setActions([...actionFields]);
  };

  const handleChangeDigitsMethod = (evt, position) => {
    const { value } = evt.target;
    const digitFields = [...digits];
    digitFields[position].action.method = value;
    setDigits([...digitFields]);
  };

  const handleChangeDigitsURL = (evt, position) => {
    const { value } = evt.target;
    const digitFields = [...digits];
    digitFields[position].action.url = value;
    setDigits([...digitFields]);
  };

  const handleClickAddDigit = () => {
    const digit = {
      _id: objectId().str,
      len: '',
      dayStart: 1,
      dayEnd: 5,
      hourStart: 0,
      hourEnd: 235959,
      ivr: data._id,
      action: {
        action: 0,
        destination: ''
      }
    };
    const digitFields = [...digits];
    digitFields.push(digit);
    setDigits([...digitFields]);
  };

  const handleClickRemoveDigit = position => {
    const digitFields = [...digits];
    digitFields.splice(position, 1);
    setDigits([...digitFields]);
  };

  const handleChangeDigitsAction = (evt, position) => {
    evt.preventDefault();
    const { value } = evt.target;
    const typeAction = parseInt(value, 10);
    const digitFields = [...digits];
    digitFields[position].action = {
      action: typeAction,
      destination: '',
      url: '',
      method: 'GET',
      headers: [],
      data: []
    };
    setDigits([...digitFields]);
  };

  const handleChangeDigitsDestination = (evt, position) => {
    evt.preventDefault();
    const { value } = evt.target;
    const digitFields = [...digits];
    if (evt.target.getAttribute('number') === 'on') {
      if (evt.target.value.match(/^\d+$/) || evt.target.value === '') {
        digitFields[position].action.destination = value;
        setDigits([...digitFields]);
      } else {
        evt.target.value = digitFields[position].action.destination;
      }
    } else {
      digitFields[position].action.destination = value;
      setDigits([...digitFields]);
    }
  };

  const handleChangeDigitsScheduling = (evt, position) => {
    evt.preventDefault();
    const { value } = evt.target;
    const digitFields = [...digits];
    const digit = { ...digitFields[position] };
    const keys = Object.keys(digit);
    if (value === 'default') {
      delete digit.dayStart;
      delete digit.dayEnd;
      delete digit.dateStart;
      delete digit.dateEnd;
      delete digit.hourStart;
      delete digit.hourEnd;
      digit.default = true;
      digitFields[position] = digit;
      setDigits([...digitFields]);
    } else if (value === 'interval') {
      if (!(keys.includes('hourStart') && keys.includes('hourEnd'))) {
        digit.hourStart = 0;
        digit.hourEnd = 2359;
      }
      delete digit.dayStart;
      delete digit.dayEnd;
      delete digit.default;
      digit.dateStart = new Date();
      digit.dateEnd = new Date();
      digitFields[position] = digit;
      setDigits([...digitFields]);
    } else {
      if (!(keys.includes('hourStart') && keys.includes('hourEnd'))) {
        digit.hourStart = 0;
        digit.hourEnd = 2359;
      }
      delete digit.dateStart;
      delete digit.dateEnd;
      delete digit.default;
      digit.dayStart = 1;
      digit.dayEnd = 5;
      digitFields[position] = digit;
      setDigits([...digitFields]);
    }
  };

  const handleChangeDigitsDay = (evt, position) => {
    evt.preventDefault();
    const { name, value } = evt.target;
    const digitFields = [...digits];
    digitFields[position][name] = value;
    setDigits([...digitFields]);
  };

  const handleChangeDigitsDate = (evt, position) => {
    const { name, value } = evt.target;
    const digitFields = [...digits];
    digitFields[position][name] = value;
    setDigits([...digitFields]);
  };

  const handleChangeDigitsHour = (evt, position) => {
    evt.preventDefault();
    const { name, value } = evt.target;
    const newValue = parseInt(value.replace(/:|_/g, ''), 10);
    const digitFields = [...digits];
    digitFields[position][name] = newValue;
    setDigits([...digitFields]);
  };

  const handleChangeDigitsIdentification = (evt, position) => {
    evt.preventDefault();
    const { value } = evt.target;
    const digitFields = [...digits];
    delete digitFields[position].len;
    delete digitFields[position].digits;
    digitFields[position][value] = '';
    setDigits([...digitFields]);
  };

  const handleChangeIdentificationValue = (evt, position) => {
    evt.preventDefault();
    const { name, value } = evt.target;
    if (name !== 'digits') {
      const newValue = parseInt(value.replace(/:|_/g, ''), 10);
      const digitFields = [...digits];
      digitFields[position][name] = newValue > -1 ? newValue : '';
      setDigits([...digitFields]);
    } else {
      const regex = /^\d{1,}$|^#$|^\*$/g;
      if (regex.test(value) || value === '') {
        const digitFields = [...digits];
        digitFields[position][name] = value;
        setDigits([...digitFields]);
      }
    }
  };

  const handleChangeRequestActions = (evt, idAction, field, position) => {
    const { value, name } = evt.target;
    const action = actions.find(action => action._id === idAction);
    const fields = [...action.action[field]];
    fields[position][name] = value.replace(/ /g, '');
    action.action[field] = [...fields];
    const newActions = actions.map(item =>
      item._id !== idAction ? item : action
    );
    setActions([...newActions]);
  };

  const handleChangeRequestDigits = (evt, idAction, field, position) => {
    const { value, name } = evt.target;
    const action = digits.find(action => action._id === idAction);
    const fields = [...action.action[field]];
    fields[position][name] = value.replace(/ /g, '');
    action.action[field] = [...fields];
    const newActions = digits.map(item =>
      item._id !== idAction ? item : action
    );
    setDigits([...newActions]);
  };

  const handleClickAddRequestActions = (idAction, field) => {
    const action = actions.find(action => action._id === idAction);
    action.action[field].push({ key: '', value: '' });
    const newActions = actions.map(item =>
      item._id !== idAction ? item : action
    );
    setActions([...newActions]);
  };

  const handleClickRemoveRequestActions = (idAction, field, position) => {
    const action = actions.find(action => action._id === idAction);
    const headers = [...action.action[field]];
    headers.splice(position, 1);
    action.action[field] = [...headers];
    const newActions = actions.map(item =>
      item._id !== idAction ? item : action
    );
    setActions([...newActions]);
  };

  const handleClickAddRequestDigits = (idAction, field) => {
    const action = digits.find(action => action._id === idAction);
    action.action[field].push({ key: '', value: '' });
    const newActions = digits.map(item =>
      item._id !== idAction ? item : action
    );
    setDigits([...newActions]);
  };

  const handleClickRemoveRequestDigits = (idAction, field, position) => {
    const action = digits.find(action => action._id === idAction);
    const headers = [...action.action[field]];
    headers.splice(position, 1);
    action.action[field] = [...headers];
    const newActions = digits.map(item =>
      item._id !== idAction ? item : action
    );
    setDigits([...newActions]);
  };

  return (
    <div className="container-fluid">
      {ivrs.editSuccess && (
        <AlertMsg type="success" msg="URA atualizado com sucesso" />
      )}
      {ivrs.errorContent &&
        !ivrs.errorContent.message.includes('400') &&
        ivrs.editError && (
        <AlertMsg type="danger" msg="Falha durante atualização da URA" />
      )}
      {ivrs.errorContent &&
        ivrs.errorContent.message.includes('400') &&
        ivrs.editError && (
        <AlertMsg
          type="danger"
          msg="O grupo informado ou nome da URA já estão em uso"
        />
      )}
      {!ivrs.editSuccess && !ivrs.editError && (
        <AlertMsg type="info" msg="Área opções da URA" />
      )}
      <FormHeader title="Atualizar URA" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="row">
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="_id">
                ID<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="_id"
                name="_id"
                autoComplete="off"
                disabled={true}
                defaultValue={data._id}
                ref={register}
              />
              {errors._id && errors._id.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="name">
                Nome<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                autoComplete="off"
                onChange={handleChange}
                defaultValue={data.name}
                ref={register}
              />
              {errors.name && errors.name.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="waitsec">
                Tempo de Espera (Segundos)
                <span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="waitsec"
                name="waitsec"
                number="on"
                autoComplete="off"
                value={data.waitsec}
                onChange={handleChange}
                ref={register}
              />
              {errors.waitsec && errors.waitsec.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.waitsec && errors.waitsec.type === 'min' && (
                <ErrorMsgField
                  type="min"
                  msg="Valor mínimo permitido maior ou igual a 0"
                />
              )}
              {errors.waitsec && errors.waitsec.type === 'max' && (
                <ErrorMsgField
                  type="max"
                  msg="Valor máximo permitido menor ou igual a 10"
                />
              )}
              {errors.waitsec && errors.waitsec.type === 'typeError' && (
                <ErrorMsgField type="typeError" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="department">
                Departamento<span className="field-required">*</span>
              </label>
              <select
                className="form-control"
                id="department"
                name="department"
                onChange={handleChange}
                value={data.department}
                ref={register}
              >
                <option value="">-- Selecione --</option>
                {departments.map((dpt, i) => {
                  const _id = dpt._id || Date.now() + '' + i;
                  return (
                    <option key={_id} value={_id}>
                      {dpt.name}
                    </option>
                  );
                })}
              </select>
              {errors.department && errors.department.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="deviceId">
                Grupo<span className="field-required">*</span>
              </label>
              <select
                className="form-control"
                id="deviceId"
                name="deviceId"
                onChange={handleChange}
                value={data.deviceId}
                ref={register}
              >
                <option value="">-- Selecione --</option>
                {devices.map((dvc, i) => {
                  const _id = dvc._id || Date.now() + '' + i;
                  return (
                    <option key={_id} value={_id}>
                      {dvc.number} | {dvc.name || '*'} - Dpto:{' '}
                      {dvc.department.name}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="saydigits">Falar Dígitos</label>
              <div className="checkbox">
                <Switch
                  type="checkbox"
                  color="success"
                  name="saydigits"
                  onChange={evt => {
                    setData({
                      ...data,
                      [evt.target.name]: evt.target.checked
                    });
                  }}
                  checked={data.saydigits}
                >
                  {data.saydigits ? 'Ativado' : 'Desativado'}
                </Switch>
              </div>
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="description">
            Descrição<span className="field-required">*</span>
          </label>
          <textarea
            className="form-control"
            id="description"
            name="description"
            rows="3"
            onChange={handleChange}
            defaultValue={data.description}
            ref={register}
          ></textarea>
          {errors.description && errors.description.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="action">
                Ação Padrão<span className="field-required">*</span>
              </label>
              <select
                className="form-control"
                id="action"
                name="action"
                onChange={handleChangeActionDefault}
                value={defaultAction.action.action}
                ref={register}
              >
                <option value="0">Desligar</option>
                <option value="1">Play Áudio</option>
                <option value="2">Transferir</option>
                <option value="3">Transferir Externo</option>
              </select>
              {errors.action && errors.action.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="destination">
                Destino Padrão<span className="field-required">*</span>
              </label>
              {defaultAction.action.action === 0 && (
                <input
                  type="text"
                  className="form-control"
                  id="destination"
                  name="destination"
                  autoComplete="off"
                  disabled={true}
                  defaultValue="Nenhum"
                  ref={register}
                />
              )}
              {defaultAction.action.action === 1 && (
                <select
                  className="form-control"
                  id="destination"
                  name="destination"
                  onChange={handleChangeActionDefault}
                  value={defaultAction.action.destination}
                  ref={register}
                >
                  <option value="">-- Selecione --</option>
                  {ivrs.data.map(ivr => (
                    <option key={ivr._id} value={ivr._id}>
                      {ivr.name}
                    </option>
                  ))}
                </select>
              )}
              {defaultAction.action.action === 2 && (
                <input
                  type="text"
                  className="form-control"
                  id="destination"
                  name="destination"
                  autoComplete="off"
                  number="on"
                  placeholder="Somente os dígitos do telefone"
                  onChange={handleChangeActionDefault}
                  defaultValue={defaultAction.action.destination}
                  ref={register}
                />
              )}
              {defaultAction.action.action === 3 && (
                <input
                  type="text"
                  className="form-control"
                  id="destination"
                  name="destination"
                  autoComplete="off"
                  number="on"
                  placeholder="Somente os dígitos do telefone"
                  onChange={handleChangeActionDefault}
                  defaultValue={defaultAction.action.destination}
                  ref={register}
                />
              )}
              {errors.destination && errors.destination.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        {/* Actions */}
        <div className="row">
          <div className="w-100 border ml-3 mr-3 mb-3">
            <div className="col-sm-12 p-2">
              <div className="form-group m-0 align-bottom">
                <label className="ml-1">Ações</label>
                <button
                  type="button"
                  className="ml-2 mb-1 btn btn-dark"
                  onClick={handleClickAddAction}
                >
                  <i className="fa fa-plus"></i>&nbsp;Adicionar
                </button>
              </div>
              {actions.map((action, position) => {
                const schedulingValue = () => {
                  const keys = Object.keys(actions[position]);
                  if (keys.includes('default')) {
                    return 'default';
                  } else if (
                    keys.includes('dateStart') &&
                    keys.includes('dateEnd')
                  ) {
                    return 'interval';
                  } else {
                    return 'day';
                  }
                };
                return (
                  /* starting actions area  */
                  <div
                    key={action._id}
                    className="row border ml-1 mr-1 mt-2 mb-2 pt-1 pb-1"
                    style={{
                      backgroundColor: `${position % 2 ? '#f2f2f2' : '#fff'}`
                    }}
                  >
                    <div className="col-sm-12">
                      <button
                        type="button"
                        className="btn btn-danger float-right action-close-button"
                        onClick={() => handleClickRemoveAction(position)}
                      >
                        <i className="fa fa-trash-alt"></i>
                      </button>
                    </div>
                    <div className="col-sm-3">
                      <div className="form-group">
                        <label htmlFor={`action_${action._id}`}>
                          Ação<span className="field-required">*</span>
                        </label>
                        <select
                          className="form-control"
                          id={`action_${action._id}`}
                          name="action"
                          onChange={evt =>
                            handleChangeActionsAction(evt, position)
                          }
                          value={actions[position].action.action}
                          ref={register}
                        >
                          <option value="0">Desligar</option>
                          <option value="1">Play Áudio</option>
                          <option value="2">Transferir</option>
                          <option value="3">Transferir Externo</option>
                          <option value="5">Consulta Web Server</option>
                        </select>
                        {errors[`action_${action._id}`] &&
                          errors[`action_${action._id}`].type ===
                            'required' && <ErrorMsgField type="required" />}
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="form-group mb-0">
                        {actions[position].action.action !== 5 && (
                          <label htmlFor={`destination_${action._id}`}>
                            Destino
                            <span className="field-required">*</span>
                          </label>
                        )}
                        {actions[position].action.action === 0 && (
                          <input
                            type="text"
                            className="form-control input-destination-action"
                            id={`destination_${action._id}`}
                            name="destination"
                            autoComplete="off"
                            disabled={true}
                            defaultValue="Nenhum"
                            ref={register}
                          />
                        )}
                        {actions[position].action.action === 1 && (
                          <select
                            className="form-control input-destination-action"
                            id={`destination_${action._id}`}
                            name="destination"
                            onChange={evt =>
                              handleChangeActionsDestination(evt, position)
                            }
                            value={actions[position].action.destination}
                            ref={register}
                          >
                            <option value="">-- Selecione --</option>
                            {ivrs.data.map(ivr => (
                              <option key={ivr._id} value={ivr._id}>
                                {ivr.name}
                              </option>
                            ))}
                          </select>
                        )}
                        {actions[position].action.action === 2 && (
                          <input
                            type="text"
                            className="form-control input-destination-action"
                            id={`destination_${action._id}`}
                            name="destination"
                            autoComplete="off"
                            number="on"
                            placeholder="Somente os dígitos do telefone"
                            onChange={evt =>
                              handleChangeActionsDestination(evt, position)
                            }
                            defaultValue={actions[position].action.destination}
                            ref={register}
                          />
                        )}
                        {actions[position].action.action === 3 && (
                          <input
                            type="text"
                            className="form-control input-destination-action"
                            id={`destination_${action._id}`}
                            name="destination"
                            autoComplete="off"
                            number="on"
                            placeholder="Somente os dígitos do telefone"
                            onChange={evt =>
                              handleChangeActionsDestination(evt, position)
                            }
                            defaultValue={actions[position].action.destination}
                            ref={register}
                          />
                        )}
                        {actions[position].action.action !== 5 &&
                          errors[`destination_${action._id}`] &&
                          errors[`destination_${action._id}`].type ===
                            'required' && <ErrorMsgField type="required" />}
                        {actions[position].action.action !== 5 &&
                          (!errors[`destination_${action._id}`] ||
                            (errors[`destination_${action._id}`] &&
                              errors[`destination_${action._id}`].type ===
                                '')) && (
                          <small className="form-text m-0">&nbsp;</small>
                        )}
                        {actions[position].action.action === 5 && (
                          <div className="row">
                            <div className="form-group mb-0 col-sm-9">
                              <label>
                                URL<span className="field-required">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control input-url"
                                id={`url_${action._id}`}
                                name="url"
                                autoComplete="off"
                                placeholder="http[s]://Servidor:Porta"
                                onChange={evt =>
                                  handleChangeActionsURL(evt, position)
                                }
                                defaultValue={actions[position].action.url}
                                ref={register}
                              />
                              {errors[`url_${action._id}`] &&
                                errors[`url_${action._id}`].type === 'url' && (
                                <ErrorMsgField type="url" />
                              )}
                              {(!errors[`url_${action._id}`] ||
                                (errors[`url_${action._id}`] &&
                                  errors[`url_${action._id}`].type === '')) && (
                                <small className="form-text m-0">&nbsp;</small>
                              )}
                            </div>
                            <div className="form-group mb-0 col-sm-3">
                              <label>
                                Método<span className="field-required">*</span>
                              </label>
                              <select
                                className="form-control"
                                id={`method_${action._id}`}
                                name="method"
                                onChange={evt =>
                                  handleChangeActionsMethod(evt, position)
                                }
                                defaultValue={actions[position].action.method}
                                ref={register}
                              >
                                <option value="GET">GET</option>
                                <option value="POST">POST</option>
                              </select>
                              <small className="form-text m-0">&nbsp;</small>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-sm-3">
                      <div className="form-group">
                        <label htmlFor={`scheduling_${action._id}`}>
                          Agendamento
                          <span className="field-required">*</span>
                        </label>
                        <select
                          className="form-control"
                          id={`scheduling_${action._id}`}
                          name="scheduling"
                          onChange={evt =>
                            handleChangeActionsScheduling(evt, position)
                          }
                          value={schedulingValue()}
                          ref={register}
                        >
                          <option value="day">Dia da Semana</option>
                          <option value="interval">Dia do Mês</option>
                        </select>
                        {errors[`scheduling_${action._id}`] &&
                          errors[`scheduling_${action._id}`].type ===
                            'required' && <ErrorMsgField type="required" />}
                      </div>
                    </div>
                    <div className="col-sm-3">
                      {Object.keys(actions[position]).includes('dayStart') && (
                        <div className="form-group mb-0">
                          <label htmlFor={`dayStart_${action._id}`}>
                            Dia Inicial
                            <span className="field-required">*</span>
                          </label>
                          <select
                            className="form-control input-day-start-action"
                            id={`dayStart_${action._id}`}
                            name="dayStart"
                            onChange={evt =>
                              handleChangeActionsDay(evt, position)
                            }
                            value={actions[position].dayStart || '1'}
                            ref={register}
                          >
                            <option value="0">Domingo</option>
                            <option value="1">Segunda</option>
                            <option value="2">Terça</option>
                            <option value="3">Quarta</option>
                            <option value="4">Quinta</option>
                            <option value="5">Sexta</option>
                            <option value="6">Sábado</option>
                          </select>
                          {errors[`dayStart_${action._id}`] &&
                            errors[`dayStart_${action._id}`].type ===
                              'required' && <ErrorMsgField type="required" />}
                          {(!errors[`dayStart_${action._id}`] ||
                            (errors[`dayStart_${action._id}`] &&
                              errors[`dayStart_${action._id}`].type ===
                                '')) && (
                            <small className="form-text m-0">&nbsp;</small>
                          )}
                        </div>
                      )}
                      {Object.keys(actions[position]).includes('dateStart') && (
                        <div className="form-group mb-0">
                          <label htmlFor={`dateStart_${action._id}`}>
                            Data Inicial
                            <span className="field-required">*</span>
                          </label>
                          <DatePicker
                            id={`dateStart_${action._id}`}
                            className="form-control input-date input-date-start-action"
                            name="dateStart"
                            onChange={date =>
                              handleChangeActionsDate(
                                {
                                  target: { value: date, name: 'dateStart' }
                                },
                                position
                              )
                            }
                            selected={actions[position].dateStart}
                            placeholderText="dd/MM/yyyy"
                            locale="ptBR"
                            dateFormat="dd/MM/yyyy"
                            autoComplete="off"
                          />
                          {errors[`dateStart_${action._id}`] &&
                            errors[`dateStart_${action._id}`].type ===
                              'date' && <ErrorMsgField type="date" />}
                          {errors[`dateStart_${action._id}`] &&
                            errors[`dateStart_${action._id}`].type ===
                              'repeat' && <ErrorMsgField type="repeat" />}
                          {(!errors[`dateStart_${action._id}`] ||
                            (errors[`dateStart_${action._id}`] &&
                              errors[`dateStart_${action._id}`].type ===
                                '')) && (
                            <small className="form-text m-0">&nbsp;</small>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="col-sm-3">
                      <div className="form-group mb-0">
                        <label htmlFor={`hourStart_${action._id}`}>
                          Hora Inicial
                          <span className="field-required">*</span>
                        </label>
                        <InputMask
                          id={`hourStart_${action._id}`}
                          name="hourStart"
                          mask="99:99"
                          defaultValue={formatTime(actions[position].hourStart)}
                          className="form-control input-hour input-hour-start-action"
                          placeholder="HH:mm"
                          autoComplete="off"
                          onChange={evt =>
                            handleChangeActionsHour(evt, position)
                          }
                          name-error={`hourStart_${action._id}`}
                          ref={register}
                        />
                        {errors[`hourStart_${action._id}`] &&
                        errors[`hourStart_${action._id}`].type === 'hour' ? (
                            <ErrorMsgField type="hour" />
                          ) : (
                            <small className="form-text m-0">&nbsp;</small>
                          )}
                      </div>
                    </div>
                    <div className="col-sm-3">
                      {Object.keys(actions[position]).includes('dayEnd') && (
                        <div className="form-group mb-0">
                          <label htmlFor={`dayEnd_${action._id}`}>
                            Dia Final
                            <span className="field-required">*</span>
                          </label>
                          <select
                            className="form-control input-day-end-action"
                            id={`dayEnd_${action._id}`}
                            name="dayEnd"
                            onChange={evt =>
                              handleChangeActionsDay(evt, position)
                            }
                            value={actions[position].dayEnd || '5'}
                            ref={register}
                          >
                            <option value="0">Domingo</option>
                            <option value="1">Segunda</option>
                            <option value="2">Terça</option>
                            <option value="3">Quarta</option>
                            <option value="4">Quinta</option>
                            <option value="5">Sexta</option>
                            <option value="6">Sábado</option>
                          </select>
                          {errors[`dayEnd_${action._id}`] &&
                            errors[`dayEnd_${action._id}`].type === 'day' && (
                            <ErrorMsgField
                              type="day"
                              msg="Final intervalo inválido"
                            />
                          )}
                          {errors[`dayEnd_${action._id}`] &&
                            errors[`dayEnd_${action._id}`].type ===
                              'required' && <ErrorMsgField type="required" />}
                          {(!errors[`dayEnd_${action._id}`] ||
                            (errors[`dayEnd_${action._id}`] &&
                              errors[`dayEnd_${action._id}`].type === '')) && (
                            <small className="form-text m-0">&nbsp;</small>
                          )}
                        </div>
                      )}
                      {Object.keys(actions[position]).includes('dateEnd') && (
                        <div className="form-group mb-0">
                          <label htmlFor={`dateEnd_${action._id}`}>
                            Data Final
                            <span className="field-required">*</span>
                          </label>
                          <DatePicker
                            id={`dateEnd_${action._id}`}
                            className="form-control input-date input-date-end-action"
                            name="dateEnd"
                            onChange={date =>
                              handleChangeActionsDate(
                                {
                                  target: { value: date, name: 'dateEnd' }
                                },
                                position
                              )
                            }
                            selected={actions[position].dateEnd}
                            placeholderText="dd/MM/yyyy"
                            locale="ptBR"
                            dateFormat="dd/MM/yyyy"
                            autoComplete="off"
                          />
                          {errors[`dateEnd_${action._id}`] &&
                            errors[`dateEnd_${action._id}`].type ===
                              'month' && (
                            <ErrorMsgField
                              type="month"
                              msg="Final intervalo inválido"
                            />
                          )}
                          {errors[`dateEnd_${action._id}`] &&
                            errors[`dateEnd_${action._id}`].type === 'date' && (
                            <ErrorMsgField type="date" />
                          )}
                          {errors[`dateEnd_${action._id}`] &&
                            errors[`dateEnd_${action._id}`].type ===
                              'repeat' && <ErrorMsgField type="repeat" />}
                          {(!errors[`dateEnd_${action._id}`] ||
                            (errors[`dateEnd_${action._id}`] &&
                              errors[`dateEnd_${action._id}`].type === '')) && (
                            <small className="form-text m-0">&nbsp;</small>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="col-sm-3">
                      <div className="form-group mb-0">
                        <label htmlFor={`hourEnd_${action._id}`}>
                          Hora Final
                          <span className="field-required">*</span>
                        </label>
                        <InputMask
                          id={`hourEnd_${action._id}`}
                          name="hourEnd"
                          mask="99:99"
                          defaultValue={formatTime(actions[position].hourEnd)}
                          className="form-control input-hour input-hour-end-action"
                          placeholder="HH:mm"
                          autoComplete="off"
                          onChange={evt =>
                            handleChangeActionsHour(evt, position)
                          }
                          name-error={`hourEnd_${position}`}
                          ref={register}
                        />
                        {errors[`hourEnd_${action._id}`] &&
                        errors[`hourEnd_${action._id}`].type === 'hour' ? (
                            <ErrorMsgField type="hour" />
                          ) : (
                            <small className="form-text m-0">&nbsp;</small>
                          )}
                      </div>
                    </div>

                    {/* init headers */}
                    {actions[position].action.action === 5 && (
                      <div className="w-100">
                        <div className="border p-2 m-3">
                          <div className="form-group m-0">
                            <label>
                              Cabeçalhos Req.
                              <span className="field-required">*</span>
                            </label>
                            <button
                              type="button"
                              className="ml-2 mb-1 btn btn-dark"
                              onClick={() =>
                                handleClickAddRequestActions(
                                  actions[position]._id,
                                  'headers'
                                )
                              }
                            >
                              <i className="fa fa-plus"></i>&nbsp;Adicionar
                            </button>
                          </div>
                          {actions[position].action.headers.map(
                            (headerReq, i) => {
                              return (
                                <div key={i} className="row mt-1">
                                  <div className="col-sm-5">
                                    <div className="form-group mb-0">
                                      <label htmlFor={`action-header-key-${i}`}>
                                        {i + 1}&deg; Chave
                                        <span className="field-required">
                                          *
                                        </span>
                                      </label>
                                      <input
                                        onChange={evt =>
                                          handleChangeRequestActions(
                                            evt,
                                            actions[position]._id,
                                            'headers',
                                            i
                                          )
                                        }
                                        type="text"
                                        id={`action-header-key-${i}`}
                                        name="key"
                                        className="form-control config-request"
                                        value={headerReq.key}
                                      />
                                      {errors[`action-header-key-${i}`] &&
                                      errors[`action-header-key-${i}`].type ===
                                        'request' ? (
                                          <ErrorMsgField type="required" />
                                        ) : (
                                          <small className="form-text m-0">
                                          &nbsp;
                                          </small>
                                        )}
                                    </div>
                                  </div>
                                  <div className="col-sm-5">
                                    <div className="form-group mb-0">
                                      <label
                                        htmlFor={`action-header-value-${i}`}
                                      >
                                        {i + 1}&deg; Valor
                                        <span className="field-required">
                                          *
                                        </span>
                                      </label>
                                      <input
                                        onChange={evt =>
                                          handleChangeRequestActions(
                                            evt,
                                            actions[position]._id,
                                            'headers',
                                            i
                                          )
                                        }
                                        type="text"
                                        id={`action-header-value-${i}`}
                                        name="value"
                                        className="form-control config-request"
                                        value={headerReq.value}
                                      />
                                      {errors[`action-header-value-${i}`] &&
                                      errors[`action-header-value-${i}`]
                                        .type === 'request' ? (
                                          <ErrorMsgField type="required" />
                                        ) : (
                                          <small className="form-text m-0">
                                          &nbsp;
                                          </small>
                                        )}
                                    </div>
                                  </div>
                                  <div className="col-sm-2 mt-4 pt-1">
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-block"
                                      onClick={() =>
                                        handleClickRemoveRequestActions(
                                          actions[position]._id,
                                          'headers',
                                          i
                                        )
                                      }
                                    >
                                      <i className="fa fa-trash-alt"></i>
                                    </button>
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    )}
                    {/* end headers */}

                    {/* init data */}
                    {actions[position].action.action === 5 && (
                      <div className="w-100">
                        <div className="border p-2 m-3">
                          <div className="form-group m-0">
                            <label>
                              Dados Req.
                              <span className="field-required">*</span>
                            </label>
                            <button
                              type="button"
                              className="ml-2 mb-1 btn btn-dark"
                              onClick={() =>
                                handleClickAddRequestActions(
                                  actions[position]._id,
                                  'data'
                                )
                              }
                            >
                              <i className="fa fa-plus"></i>&nbsp;Adicionar
                            </button>
                          </div>
                          {actions[position].action.data.map((dataReq, i) => {
                            return (
                              <div key={i} className="row mt-1">
                                <div className="col-sm-5">
                                  <div className="form-group mb-0">
                                    <label htmlFor={`action-data-key-${i}`}>
                                      {i + 1}&deg; Chave
                                      <span className="field-required">*</span>
                                    </label>
                                    <input
                                      onChange={evt =>
                                        handleChangeRequestActions(
                                          evt,
                                          actions[position]._id,
                                          'data',
                                          i
                                        )
                                      }
                                      type="text"
                                      id={`action-data-key-${i}`}
                                      name="key"
                                      className="form-control config-request"
                                      value={dataReq.key}
                                    />
                                    {errors[`action-data-key-${i}`] &&
                                    errors[`action-data-key-${i}`].type ===
                                      'request' ? (
                                        <ErrorMsgField type="required" />
                                      ) : (
                                        <small className="form-text m-0">
                                        &nbsp;
                                        </small>
                                      )}
                                  </div>
                                </div>
                                <div className="col-sm-5">
                                  <div className="form-group mb-0">
                                    <label htmlFor={`action-data-value-${i}`}>
                                      {i + 1}&deg; Valor
                                      <span className="field-required">*</span>
                                    </label>
                                    <input
                                      onChange={evt =>
                                        handleChangeRequestActions(
                                          evt,
                                          actions[position]._id,
                                          'data',
                                          i
                                        )
                                      }
                                      type="text"
                                      id={`action-data-value-${i}`}
                                      name="value"
                                      className="form-control config-request"
                                      value={dataReq.value}
                                    />
                                    {errors[`action-data-value-${i}`] &&
                                    errors[`action-data-value-${i}`].type ===
                                      'request' ? (
                                        <ErrorMsgField type="required" />
                                      ) : (
                                        <small className="form-text m-0">
                                        &nbsp;
                                        </small>
                                      )}
                                  </div>
                                </div>
                                <div className="col-sm-2 mt-4 pt-1">
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-block"
                                    onClick={() =>
                                      handleClickRemoveRequestActions(
                                        actions[position]._id,
                                        'data',
                                        i
                                      )
                                    }
                                  >
                                    <i className="fa fa-trash-alt"></i>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {/* end data */}
                  </div>
                  /* finish actions area  */
                );
              })}
            </div>
          </div>
        </div>
        {/* End Actions */}
        {/* Digits */}
        <div className="row">
          <div className="w-100 border ml-3 mr-3 mb-3">
            <div className="col-sm-12 p-2">
              <div className="form-group m-0 align-bottom">
                <label className="ml-1">Digitos</label>
                <button
                  type="button"
                  className="ml-2 mb-1 btn btn-dark"
                  onClick={handleClickAddDigit}
                >
                  <i className="fa fa-plus"></i>&nbsp;Adicionar
                </button>
              </div>
              {digits.map((digit, position) => {
                const schedulingValue = () => {
                  const keys = Object.keys(digits[position]);
                  if (keys.includes('default')) {
                    return 'default';
                  } else if (
                    keys.includes('dateStart') &&
                    keys.includes('dateEnd')
                  ) {
                    return 'interval';
                  } else {
                    return 'day';
                  }
                };
                return (
                  /* starting digits area  */
                  <div
                    key={digit._id}
                    className="row border ml-1 mr-1 mt-2 mb-2 pt-1 pb-1"
                    style={{
                      backgroundColor: `${position % 2 ? '#f2f2f2' : '#fff'}`
                    }}
                  >
                    <div className="col-sm-12">
                      <button
                        type="button"
                        className="btn btn-danger float-right action-close-button"
                        onClick={() => handleClickRemoveDigit(position)}
                      >
                        <i className="fa fa-trash-alt"></i>
                      </button>
                    </div>
                    <div className="col-sm-6">
                      <div className="form-group">
                        <label htmlFor={`identification_${digit._id}`}>
                          Identificação
                          <span className="field-required">*</span>
                        </label>
                        <select
                          className="form-control"
                          id={`identification_${digit._id}`}
                          name="identification"
                          onChange={evt =>
                            handleChangeDigitsIdentification(evt, position)
                          }
                          value={
                            Object.keys(digits[position]).includes('len')
                              ? 'len'
                              : 'digits'
                          }
                          ref={register}
                        >
                          <option value="len">Tamanho</option>
                          <option value="digits">Dígito</option>
                        </select>
                        {errors[`action_${digit._id}`] &&
                          errors[`action_${digit._id}`].type === 'required' && (
                          <ErrorMsgField type="required" />
                        )}
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="form-group mb-0">
                        <label htmlFor={`identificationValue_${digit._id}`}>
                          Tamanho / Dígitos
                          <span className="field-required">*</span>
                        </label>
                        {Object.keys(digit).includes('len') ? (
                          <input
                            type="text"
                            className="form-control input-identification-value"
                            id={`identificationValue_${digit._id}`}
                            name="len"
                            autoComplete="off"
                            placeholder="Qual a quantidade de dígitos"
                            onChange={evt =>
                              handleChangeIdentificationValue(evt, position)
                            }
                            value={
                              digits[position].len > -1
                                ? digits[position].len
                                : ''
                            }
                            ref={register}
                          />
                        ) : (
                          <input
                            type="text"
                            className="form-control input-identification-value"
                            id={`identificationValue_${digit._id}`}
                            name="digits"
                            autoComplete="off"
                            placeholder="Quais dígitos"
                            onChange={evt =>
                              handleChangeIdentificationValue(evt, position)
                            }
                            value={digits[position].digits || ''}
                            ref={register}
                          />
                        )}
                        {errors[`identificationValue_${digit._id}`] &&
                          errors[`identificationValue_${digit._id}`].type ===
                            'required' && <ErrorMsgField type="required" />}
                        {(!errors[`identificationValue_${digit._id}`] ||
                          (errors[`identificationValue_${digit._id}`] &&
                            errors[`identificationValue_${digit._id}`].type ===
                              '')) && (
                          <small className="form-text m-0">&nbsp;</small>
                        )}
                      </div>
                    </div>
                    <div className="col-sm-3">
                      <div className="form-group">
                        <label htmlFor={`action_${digit._id}`}>
                          Ação<span className="field-required">*</span>
                        </label>
                        <select
                          className="form-control"
                          id={`action_${digit._id}`}
                          name="action"
                          onChange={evt =>
                            handleChangeDigitsAction(evt, position)
                          }
                          value={digits[position].action.action}
                          ref={register}
                        >
                          <option value="0">Desligar</option>
                          <option value="1">Play Áudio</option>
                          <option value="2">Transferir</option>
                          <option value="3">Transferir Externo</option>
                          <option value="5">Consulta Web Server</option>
                          <option value="6">Pesquisa de Satisfação</option>
                        </select>
                        {errors[`action_${digit._id}`] &&
                          errors[`action_${digit._id}`].type === 'required' && (
                          <ErrorMsgField type="required" />
                        )}
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="form-group mb-0">
                        {digits[position].action.action !== 5 && (
                          <label htmlFor={`destination_${digit._id}`}>
                            Destino
                            <span className="field-required">*</span>
                          </label>
                        )}
                        {digits[position].action.action === 0 && (
                          <input
                            type="text"
                            className="form-control input-destination-digit"
                            id={`destination_${digit._id}`}
                            name="destination"
                            autoComplete="off"
                            disabled={true}
                            defaultValue="Nenhum"
                            ref={register}
                          />
                        )}
                        {digits[position].action.action === 1 && (
                          <select
                            className="form-control input-destination-digit"
                            id={`destination_${digit._id}`}
                            name="destination"
                            onChange={evt =>
                              handleChangeDigitsDestination(evt, position)
                            }
                            value={digits[position].action.destination}
                            ref={register}
                          >
                            <option value="">-- Selecione --</option>
                            {ivrs.data.map(ivr => (
                              <option key={ivr._id} value={ivr._id}>
                                {ivr.name}
                              </option>
                            ))}
                          </select>
                        )}
                        {digits[position].action.action === 2 && (
                          <input
                            type="text"
                            className="form-control input-destination-digit"
                            id={`destination_${digit._id}`}
                            name="destination"
                            autoComplete="off"
                            number="on"
                            placeholder="Somente os dígitos do telefone"
                            onChange={evt =>
                              handleChangeDigitsDestination(evt, position)
                            }
                            defaultValue={digits[position].action.destination}
                            ref={register}
                          />
                        )}
                        {digits[position].action.action === 3 && (
                          <input
                            type="text"
                            className="form-control input-destination-digit"
                            id={`destination_${digit._id}`}
                            name="destination"
                            autoComplete="off"
                            number="on"
                            placeholder="Somente os dígitos do telefone"
                            onChange={evt =>
                              handleChangeDigitsDestination(evt, position)
                            }
                            defaultValue={digits[position].action.destination}
                            ref={register}
                          />
                        )}
                        {digits[position].action.action === 5 && (
                          <div className="row">
                            <div className="form-group mb-0 col-sm-9">
                              <label>
                                URL<span className="field-required">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control input-url"
                                id={`url_${digit._id}`}
                                name="url"
                                autoComplete="off"
                                placeholder="http[s]://Servidor:Porta"
                                onChange={evt =>
                                  handleChangeDigitsURL(evt, position)
                                }
                                defaultValue={digits[position].action.url}
                                ref={register}
                              />
                              {errors[`url_${digit._id}`] &&
                                errors[`url_${digit._id}`].type === 'url' && (
                                <ErrorMsgField type="url" />
                              )}
                              {(!errors[`url_${digit._id}`] ||
                                (errors[`url_${digit._id}`] &&
                                  errors[`url_${digit._id}`].type === '')) && (
                                <small className="form-text m-0">&nbsp;</small>
                              )}
                            </div>
                            <div className="form-group mb-0 col-sm-3">
                              <label>
                                Método<span className="field-required">*</span>
                              </label>
                              <select
                                className="form-control"
                                id={`method_${digit._id}`}
                                name="method"
                                onChange={evt =>
                                  handleChangeDigitsMethod(evt, position)
                                }
                                defaultValue={digits[position].action.method}
                                ref={register}
                              >
                                <option value="GET">GET</option>
                                <option value="POST">POST</option>
                              </select>
                              <small className="form-text m-0">&nbsp;</small>
                            </div>
                          </div>
                        )}
                        {digits[position].action.action === 6 && (
                          <select
                            className="form-control input-destination-digit"
                            id={`destination_${digit._id}`}
                            name="destination"
                            onChange={evt =>
                              handleChangeDigitsDestination(evt, position)
                            }
                            value={digits[position].action.destination}
                            ref={register}
                          >
                            <option value="">-- Selecione --</option>
                            {ivrs.data.map(ivr => (
                              <option key={ivr._id} value={ivr._id}>
                                {ivr.name}
                              </option>
                            ))}
                          </select>
                        )}
                        {errors[`destination_${digit._id}`] &&
                          errors[`destination_${digit._id}`].type ===
                            'required' && <ErrorMsgField type="required" />}
                        {digits[position].action.action !== 5 &&
                          (!errors[`destination_${digit._id}`] ||
                            (errors[`destination_${digit._id}`] &&
                              errors[`destination_${digit._id}`].type ===
                                '')) && (
                          <small className="form-text m-0">&nbsp;</small>
                        )}
                      </div>
                    </div>
                    <div className="col-sm-3">
                      <div className="form-group">
                        <label htmlFor={`scheduling_${digit._id}`}>
                          Agendamento
                          <span className="field-required">*</span>
                        </label>
                        <select
                          className="form-control"
                          id={`scheduling_${digit._id}`}
                          name="scheduling"
                          onChange={evt =>
                            handleChangeDigitsScheduling(evt, position)
                          }
                          value={schedulingValue()}
                          ref={register}
                        >
                          <option value="default">Padrão</option>
                          <option value="day">Dia da Semana</option>
                          <option value="interval">Dia do Mês</option>
                        </select>
                        {errors[`scheduling_${digit._id}`] &&
                          errors[`scheduling_${digit._id}`].type ===
                            'required' && <ErrorMsgField type="required" />}
                      </div>
                    </div>
                    <div className="col-sm-3">
                      {(Object.keys(digits[position]).includes('dayStart') ||
                        Object.keys(digits[position]).includes('default')) && (
                        <div className="form-group mb-0">
                          <label htmlFor={`dayStart_${digit._id}`}>
                            Dia Inicial
                            <span className="field-required">*</span>
                          </label>
                          <select
                            className="form-control input-day-start-digit"
                            id={`dayStart_${digit._id}`}
                            name="dayStart"
                            onChange={evt =>
                              handleChangeDigitsDay(evt, position)
                            }
                            value={digits[position].dayStart || '1'}
                            disabled={Object.keys(digits[position]).includes(
                              'default'
                            )}
                            ref={register}
                          >
                            <option value="0">Domingo</option>
                            <option value="1">Segunda</option>
                            <option value="2">Terça</option>
                            <option value="3">Quarta</option>
                            <option value="4">Quinta</option>
                            <option value="5">Sexta</option>
                            <option value="6">Sábado</option>
                          </select>
                          {errors[`dayStart_${digit._id}`] &&
                            errors[`dayStart_${digit._id}`].type ===
                              'required' && <ErrorMsgField type="required" />}
                          {(!errors[`dayStart_${digit._id}`] ||
                            (errors[`dayStart_${digit._id}`] &&
                              errors[`dayStart_${digit._id}`].type === '')) && (
                            <small className="form-text m-0">&nbsp;</small>
                          )}
                        </div>
                      )}
                      {Object.keys(digits[position]).includes('dateStart') && (
                        <div className="form-group mb-0">
                          <label htmlFor={`dateStart_${digit._id}`}>
                            Data Inicial
                            <span className="field-required">*</span>
                          </label>
                          <DatePicker
                            id={`dateStart_${digit._id}`}
                            className="form-control input-date input-date-start-digit"
                            name="dateStart"
                            onChange={date =>
                              handleChangeDigitsDate(
                                {
                                  target: { value: date, name: 'dateStart' }
                                },
                                position
                              )
                            }
                            selected={digits[position].dateStart}
                            placeholderText="dd/MM/yyyy"
                            locale="ptBR"
                            dateFormat="dd/MM/yyyy"
                            autoComplete="off"
                          />
                          {errors[`dateStart_${digit._id}`] &&
                            errors[`dateStart_${digit._id}`].type ===
                              'date' && <ErrorMsgField type="date" />}
                          {errors[`dateStart_${digit._id}`] &&
                            errors[`dateStart_${digit._id}`].type ===
                              'repeat' && <ErrorMsgField type="repeat" />}
                          {(!errors[`dateStart_${digit._id}`] ||
                            (errors[`dateStart_${digit._id}`] &&
                              errors[`dateStart_${digit._id}`].type ===
                                '')) && (
                            <small className="form-text m-0">&nbsp;</small>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="col-sm-3">
                      <div className="form-group mb-0">
                        <label htmlFor={`hourStart_${digit._id}`}>
                          Hora Inicial
                          <span className="field-required">*</span>
                        </label>
                        {!Object.keys(digits[position]).includes('default') ? (
                          <InputMask
                            id={`hourStart_${digit._id}`}
                            name="hourStart"
                            mask="99:99"
                            defaultValue={formatTime(
                              digits[position].hourStart
                            )}
                            className="form-control input-hour input-hour-start-digit"
                            placeholder="HH:mm"
                            autoComplete="off"
                            onChange={evt =>
                              handleChangeDigitsHour(evt, position)
                            }
                            name-error={`hourStart_${digit._id}`}
                            ref={register}
                          />
                        ) : (
                          <input
                            type="text"
                            id={`hourStart_${digit._id}`}
                            name="hourStart"
                            defaultValue={'00:00'}
                            className="form-control input-hour input-hour-start-digit"
                            autoComplete="off"
                            disabled={true}
                            ref={register}
                          />
                        )}
                        {errors[`hourStart_${digit._id}`] &&
                        errors[`hourStart_${digit._id}`].type === 'hour' ? (
                            <ErrorMsgField type="hour" />
                          ) : (
                            <small className="form-text m-0">&nbsp;</small>
                          )}
                      </div>
                    </div>
                    <div className="col-sm-3">
                      {(Object.keys(digits[position]).includes('dayEnd') ||
                        Object.keys(digits[position]).includes('default')) && (
                        <div className="form-group mb-0">
                          <label htmlFor={`dayEnd_${digit._id}`}>
                            Dia Final
                            <span className="field-required">*</span>
                          </label>
                          <select
                            className="form-control input-day-end-digit"
                            id={`dayEnd_${digit._id}`}
                            name="dayEnd"
                            onChange={evt =>
                              handleChangeDigitsDay(evt, position)
                            }
                            value={digits[position].dayEnd || '5'}
                            disabled={Object.keys(digits[position]).includes(
                              'default'
                            )}
                            ref={register}
                          >
                            <option value="0">Domingo</option>
                            <option value="1">Segunda</option>
                            <option value="2">Terça</option>
                            <option value="3">Quarta</option>
                            <option value="4">Quinta</option>
                            <option value="5">Sexta</option>
                            <option value="6">Sábado</option>
                          </select>
                          {errors[`dayEnd_${digit._id}`] &&
                            errors[`dayEnd_${digit._id}`].type === 'day' && (
                            <ErrorMsgField
                              type="day"
                              msg="Final intervalo inválido"
                            />
                          )}
                          {errors[`dayEnd_${digit._id}`] &&
                            errors[`dayEnd_${digit._id}`].type ===
                              'required' && <ErrorMsgField type="required" />}
                          {(!errors[`dayEnd_${digit._id}`] ||
                            (errors[`dayEnd_${digit._id}`] &&
                              errors[`dayEnd_${digit._id}`].type === '')) && (
                            <small className="form-text m-0">&nbsp;</small>
                          )}
                        </div>
                      )}
                      {Object.keys(digits[position]).includes('dateEnd') && (
                        <div className="form-group mb-0">
                          <label htmlFor={`dateEnd_${digit._id}`}>
                            Data Final
                            <span className="field-required">*</span>
                          </label>
                          <DatePicker
                            id={`dateEnd_${digit._id}`}
                            className="form-control input-date input-date-end-digit"
                            name="dateEnd"
                            onChange={date =>
                              handleChangeDigitsDate(
                                {
                                  target: { value: date, name: 'dateEnd' }
                                },
                                position
                              )
                            }
                            selected={digits[position].dateEnd}
                            placeholderText="dd/MM/yyyy"
                            locale="ptBR"
                            dateFormat="dd/MM/yyyy"
                            autoComplete="off"
                          />
                          {errors[`dateEnd_${digit._id}`] &&
                            errors[`dateEnd_${digit._id}`].type === 'month' && (
                            <ErrorMsgField
                              type="month"
                              msg="Final intervalo inválido"
                            />
                          )}
                          {errors[`dateEnd_${digit._id}`] &&
                            errors[`dateEnd_${digit._id}`].type === 'date' && (
                            <ErrorMsgField type="date" />
                          )}
                          {errors[`dateEnd_${digit._id}`] &&
                            errors[`dateEnd_${digit._id}`].type ===
                              'repeat' && <ErrorMsgField type="repeat" />}
                          {(!errors[`dateEnd_${digit._id}`] ||
                            (errors[`dateEnd_${digit._id}`] &&
                              errors[`dateEnd_${digit._id}`].type === '')) && (
                            <small className="form-text m-0">&nbsp;</small>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="col-sm-3">
                      <div className="form-group mb-0">
                        <label htmlFor={`hourEnd_${digit._id}`}>
                          Hora Final
                          <span className="field-required">*</span>
                        </label>
                        {!Object.keys(digits[position]).includes('default') ? (
                          <InputMask
                            id={`hourEnd_${digit._id}`}
                            name="hourEnd"
                            mask="99:99"
                            defaultValue={formatTime(digits[position].hourEnd)}
                            className="form-control input-hour input-hour-end-digit"
                            placeholder="HH:mm"
                            autoComplete="off"
                            onChange={evt =>
                              handleChangeDigitsHour(evt, position)
                            }
                            name-error={`hourEnd_${position}`}
                            ref={register}
                          />
                        ) : (
                          <input
                            type="text"
                            id={`hourEnd_${digit._id}`}
                            name="hourEnd"
                            defaultValue={'23:59'}
                            className="form-control input-hour input-hour-end-digit"
                            autoComplete="off"
                            disabled={true}
                            ref={register}
                          />
                        )}
                        {errors[`hourEnd_${digit._id}`] &&
                        errors[`hourEnd_${digit._id}`].type === 'hour' ? (
                            <ErrorMsgField type="hour" />
                          ) : (
                            <small className="form-text m-0">&nbsp;</small>
                          )}
                      </div>
                    </div>
                    {/* Req Digits */}
                    {/* init headers */}
                    {digits[position].action.action === 5 && (
                      <div className="w-100">
                        <div className="border p-2 m-3">
                          <div className="form-group m-0">
                            <label>
                              Cabeçalhos Req.
                              <span className="field-required">*</span>
                            </label>
                            <button
                              type="button"
                              className="ml-2 mb-1 btn btn-dark"
                              onClick={() =>
                                handleClickAddRequestDigits(
                                  digits[position]._id,
                                  'headers'
                                )
                              }
                            >
                              <i className="fa fa-plus"></i>&nbsp;Adicionar
                            </button>
                          </div>
                          {digits[position].action.headers.map(
                            (headerReq, i) => {
                              return (
                                <div key={i} className="row mt-1">
                                  <div className="col-sm-5">
                                    <div className="form-group mb-0">
                                      <label htmlFor={`digit-header-key-${i}`}>
                                        {i + 1}&deg; Chave
                                        <span className="field-required">
                                          *
                                        </span>
                                      </label>
                                      <input
                                        onChange={evt =>
                                          handleChangeRequestDigits(
                                            evt,
                                            digits[position]._id,
                                            'headers',
                                            i
                                          )
                                        }
                                        type="text"
                                        id={`digit-header-key-${i}`}
                                        name="key"
                                        className="form-control config-request"
                                        value={headerReq.key}
                                      />
                                      {errors[`digit-header-key-${i}`] &&
                                      errors[`digit-header-key-${i}`].type ===
                                        'request' ? (
                                          <ErrorMsgField type="required" />
                                        ) : (
                                          <small className="form-text m-0">
                                          &nbsp;
                                          </small>
                                        )}
                                    </div>
                                  </div>
                                  <div className="col-sm-5">
                                    <div className="form-group mb-0">
                                      <label
                                        htmlFor={`digit-header-value-${i}`}
                                      >
                                        {i + 1}&deg; Valor
                                        <span className="field-required">
                                          *
                                        </span>
                                      </label>
                                      <input
                                        onChange={evt =>
                                          handleChangeRequestDigits(
                                            evt,
                                            digits[position]._id,
                                            'headers',
                                            i
                                          )
                                        }
                                        type="text"
                                        id={`digit-header-value-${i}`}
                                        name="value"
                                        className="form-control config-request"
                                        value={headerReq.value}
                                      />
                                      {errors[`digit-header-value-${i}`] &&
                                      errors[`digit-header-value-${i}`].type ===
                                        'request' ? (
                                          <ErrorMsgField type="required" />
                                        ) : (
                                          <small className="form-text m-0">
                                          &nbsp;
                                          </small>
                                        )}
                                    </div>
                                  </div>
                                  <div className="col-sm-2 mt-4 pt-1">
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-block"
                                      onClick={() =>
                                        handleClickRemoveRequestDigits(
                                          digits[position]._id,
                                          'headers',
                                          i
                                        )
                                      }
                                    >
                                      <i className="fa fa-trash-alt"></i>
                                    </button>
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    )}
                    {/* end headers */}

                    {/* init data */}
                    {digits[position].action.action === 5 && (
                      <div className="w-100">
                        <div className="border p-2 m-3">
                          <div className="form-group m-0">
                            <label>
                              Dados Req.
                              <span className="field-required">*</span>
                            </label>
                            <button
                              type="button"
                              className="ml-2 mb-1 btn btn-dark"
                              onClick={() =>
                                handleClickAddRequestDigits(
                                  digits[position]._id,
                                  'data'
                                )
                              }
                            >
                              <i className="fa fa-plus"></i>&nbsp;Adicionar
                            </button>
                          </div>
                          {digits[position].action.data.map((dataReq, i) => {
                            return (
                              <div key={i} className="row mt-1">
                                <div className="col-sm-5">
                                  <div className="form-group mb-0">
                                    <label htmlFor={`digit-data-key-${i}`}>
                                      {i + 1}&deg; Chave
                                      <span className="field-required">*</span>
                                    </label>
                                    <input
                                      onChange={evt =>
                                        handleChangeRequestDigits(
                                          evt,
                                          digits[position]._id,
                                          'data',
                                          i
                                        )
                                      }
                                      type="text"
                                      id={`digit-data-key-${i}`}
                                      name="key"
                                      className="form-control config-request"
                                      value={dataReq.key}
                                    />
                                    {errors[`digit-data-key-${i}`] &&
                                    errors[`digit-data-key-${i}`].type ===
                                      'request' ? (
                                        <ErrorMsgField type="required" />
                                      ) : (
                                        <small className="form-text m-0">
                                        &nbsp;
                                        </small>
                                      )}
                                  </div>
                                </div>
                                <div className="col-sm-5">
                                  <div className="form-group mb-0">
                                    <label htmlFor={`digit-data-value-${i}`}>
                                      {i + 1}&deg; Valor
                                      <span className="field-required">*</span>
                                    </label>
                                    <input
                                      onChange={evt =>
                                        handleChangeRequestDigits(
                                          evt,
                                          digits[position]._id,
                                          'data',
                                          i
                                        )
                                      }
                                      type="text"
                                      id={`digit-data-value-${i}`}
                                      name="value"
                                      className="form-control config-request"
                                      value={dataReq.value}
                                    />
                                    {errors[`digit-data-value-${i}`] &&
                                    errors[`digit-data-value-${i}`].type ===
                                      'request' ? (
                                        <ErrorMsgField type="required" />
                                      ) : (
                                        <small className="form-text m-0">
                                        &nbsp;
                                        </small>
                                      )}
                                  </div>
                                </div>
                                <div className="col-sm-2 mt-4 pt-1">
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-block"
                                    onClick={() =>
                                      handleClickRemoveRequestDigits(
                                        digits[position]._id,
                                        'data',
                                        i
                                      )
                                    }
                                  >
                                    <i className="fa fa-trash-alt"></i>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {/* end data */}
                  </div>
                  /* finish digits area  */
                );
              })}
            </div>
          </div>
        </div>
        {/* End Digits */}
        <FormFooter url="/ivrs" reference={submitButton} />
      </form>
    </div>
  );
}

Options.propTypes = {
  match: PropTypes.object
};

export default Options;
